import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {GetAnalysisDataFrame} from 'analysis-tool/AnalysisTypes';
import SaveToResultsConfigurationModal, {
  SaveToResultsConfigurationModalProps,
} from 'analysis-tool/structured-data-mapping/SaveToResultsConfigurationModal';
import {SIDEBAR_FAILED_ICON, SIDEBAR_SPINNER_ICON} from 'analysis-tool/ui/sidebar/SidebarItemDatas';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ButtonSize} from 'ui-library/buttons/Button';
import Checkbox from 'ui-library/Checkbox';
import {RED_LIGHT, GRAY_SEMIDARK} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import runModal from 'ui-library/runModal';
import {assertNever} from 'util/Types';

import {isSendToEntryEnabled} from './OutputsUtil';
import {AnalysisOutput, AnalysisOutputConfig, AnalysisOutputType, AnalysisOutputOptionItem} from './Types';

/** @package */
export interface AnalysisOutputOptionProps {
  clientStateId: string;
  onUpdateOutputConfig: (outputConfig: AnalysisOutputConfig) => void;
  onUpdateSelectedOutput: (isSelected: boolean) => void;
  option: AnalysisOutputOptionItem;
  selectedOutputs: Array<AnalysisOutput>;
}

function analysisOutputTypeToDisplayName(outputConfig: AnalysisOutputConfig): React.ReactNode {
  switch (outputConfig.type) {
    case AnalysisOutputType.CHART:
      return 'As chart';
    case AnalysisOutputType.DATASET:
      return 'As dataset';
    case AnalysisOutputType.RESULT: {
      const maybeTarget = outputConfig.targetSpecificMappingState?.target;
      return (
        <>
          As {}
          <span className={css(styles.bold)}>{outputConfig.newDataMapping.resultSchema!.name}</span>
          {} result
          {maybeTarget != null && (
            <>
              {} for {}
              <span className={css(styles.bold)}>{maybeTarget}</span>
            </>
          )}
        </>
      );
    }
    default:
      throw new Error(`Unexpected AnalysisOutputType: ${assertNever(outputConfig)}`);
  }
}

/** @package */
export function runSaveToResultsModal(
  clientStateId: string,
  inputDataFrame: GetAnalysisDataFrame,
  onSubmit: SaveToResultsConfigurationModalProps['onRequestSubmit']
): void {
  runModal(SaveToResultsConfigurationModal, {
    clientStateId,
    dataFrame: inputDataFrame,
    // Closing the modal should noop since the user opted out of the save to results flow
    onRequestClose: noop,
    onRequestSubmit: onSubmit,
  });
}

/** @package */
export function isOutputOptionDisabled(outputOption: AnalysisOutputOptionItem): boolean {
  return outputOption.outputConfigs.length === 0;
}

export function AnalysisOutputSelectorOption({
  clientStateId,
  onUpdateOutputConfig,
  onUpdateSelectedOutput,
  option,
  selectedOutputs,
}: AnalysisOutputOptionProps): JSX.Element | null {
  const icon = option.sidebarItemData.icon;
  const isDisabled = isOutputOptionDisabled(option);
  const matchingSelectedOutput = selectedOutputs.find(
    (selectedOutput) => selectedOutput.selectorIndex === option.selectorIndex
  );
  return (
    <div
      className={css(styles.option)}
      style={{marginLeft: 20 * option.indentLevel}}
      {...getTestProps({component: 'AnalysisOutputSelectorOption', element: 'root'})}
    >
      <Tooltip isDisabled={option.disabledReason == null} text={option.disabledReason}>
        <span
          className={css(styles.optionLeftSide)}
          onClick={() => {
            onUpdateSelectedOutput(!matchingSelectedOutput);
          }}
        >
          <Checkbox checked={matchingSelectedOutput != null} disabled={isDisabled} />
          <span className={classNames(isDisabled && css(styles.disabledOption))}>
            <BenchlingIcon
              icon={icon}
              className={classNames(
                'u-mini-spacing-left',
                icon === SIDEBAR_SPINNER_ICON && 'fa-spin',
                icon === SIDEBAR_FAILED_ICON && css(styles.errorIcon)
              )}
            />
            <span className="u-mini-spacing-left">
              {option?.outputConfigs.length > 1
                ? option.outputConfigs[0].outputName
                : option.sidebarItemData.title}
            </span>
          </span>
        </span>
      </Tooltip>
      {matchingSelectedOutput && (
        <SelectDropdown
          buttonSize={ButtonSize.MINI}
          disabled={!BenchlingConfig.ENABLE_ANALYSIS_TOOL_SEND_TO_WORKSHEET && !isSendToEntryEnabled()}
          menu={
            <Menu height={MenuHeight.SMALL} width={MenuWidth.FULL}>
              <Option
                value={AnalysisOutputType.DATASET}
                onSelect={() => {
                  const datasetOption = option.outputConfigs.find(
                    (outputConfig) => outputConfig.type === AnalysisOutputType.DATASET
                  )! as AnalysisOutputConfig;
                  onUpdateOutputConfig(datasetOption);
                }}
              >
                As dataset
              </Option>
              {option.outputConfigs.find(
                (outputConfig) => outputConfig.type === AnalysisOutputType.CHART
              ) && (
                <Option
                  value={AnalysisOutputType.CHART}
                  onSelect={() => {
                    const chartOption = option.outputConfigs.find(
                      (outputConfig) => outputConfig.type === AnalysisOutputType.CHART
                    )! as AnalysisOutputConfig;
                    onUpdateOutputConfig(chartOption);
                  }}
                >
                  As chart
                </Option>
              )}
              <Option
                value={AnalysisOutputType.RESULT}
                onSelect={() => {
                  const resultOption = option.outputConfigs.find(
                    (outputConfig) => outputConfig.type === AnalysisOutputType.RESULT
                  )!;
                  // Assert for type check
                  assert(resultOption.type === AnalysisOutputType.RESULT, 'Expected result output config');
                  runSaveToResultsModal(
                    clientStateId,
                    resultOption.dataFrame,
                    ({dataMapping, targetSpecificMappingState}) => {
                      const resultOption = option.outputConfigs.find(
                        (outputConfig) => outputConfig.type === AnalysisOutputType.RESULT
                      )!;
                      // Assert for type check
                      assert(
                        resultOption.type === AnalysisOutputType.RESULT,
                        'Expected result output config'
                      );
                      onUpdateOutputConfig({
                        ...resultOption,
                        newDataMapping: dataMapping,
                        targetSpecificMappingState,
                      });
                    }
                  );
                }}
              >
                As result
              </Option>
            </Menu>
          }
          displayName={analysisOutputTypeToDisplayName(matchingSelectedOutput.outputConfig)}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  option: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    // This height needs to be the height of the dropdown button, so that selecting an option doesn't cause
    // the elements to readjust
    height: 28,
  },
  optionLeftSide: {display: 'flex', alignItems: 'center', width: 'max-content', cursor: 'pointer'},
  disabledOption: {color: GRAY_SEMIDARK},
  errorIcon: {
    color: RED_LIGHT,
  },
  bold: {fontWeight: 'bold'},
});
